import * as React from 'react';
import * as _ from 'lodash';
import * as classnames from 'classnames';
import { connect } from 'react-redux';
import {
    CONDITION_TYPES,
    Project,
    ProjectLanguage,
    QTYPES,
    Question,
    QuestionSummary
} from '@nexxt/common/types';
import IncaVariableSelect from '../IncaVariableSelect';
import { CONDITION_QTYPES } from '../../../../constants/question';
import { IApplicationState } from '../../../../reducers';
import { getSystemQuestionSummaries } from '../../../../utils/question';
import { getQuestionByQuestionId } from '../../../../services/question.service';
import IncaVariableEval from '@nexxt/common/incaLogic/IncaVariableEval';
import {
    getOrdinalNumber,
    uppercaseFirstLetter
} from '@nexxt/common/services/TextService';
import { IncaVariableFunctions } from '@nexxt/common/incaLogic/constants';
import { UilExclamationTriangle } from '@iconscout/react-unicons';
import ReactTooltip from 'react-tooltip';
import { VariableFunctionEditor, VariableOptionEditor } from '../common';
import { URL_METADATA_QUESTION_VALUE } from '@nexxt/common/constants';
import { Notes } from '../../notes';
import { ALERT_MESSAGE_TYPES } from '../../../../interface/project.interface';
import { INCA_VARIABLE_FUNCTION_TYPES } from '@nexxt/common/incaLogic/types';

interface IncaVariableEvalEditorProps {
    isEdit: boolean;
    rule: any;
    disabled: boolean;
    className?: string;
    showError?: boolean;
    errorObject?: any;
    handleQuestionSelect: (questionSummary: QuestionSummary) => void;
    handleVariableFunctionSelect: (variableFunction) => void;
    handleOptionSelect: (type, option, compareType?) => void;
    questionSummaries: QuestionSummary[];
    currentProject: Project;
    languages: ProjectLanguage[];
    projectID: number;
    systemQuestionSummaries: {
        [value: string]: QuestionSummary;
    };
    getQuestionById: (questionId: number, options: any) => Promise<Question>;
}

const IncaVariableEvalEditor: React.FC<IncaVariableEvalEditorProps> = (
    props: IncaVariableEvalEditorProps
) => {
    const [options, setOptions] = React.useState([]);
    const [targets, setTargets] = React.useState([]);
    const [resetSelectedOptions, setResetSelectedOptions] =
        React.useState(false);
    const [variableFunctions, setVariableFunctions] = React.useState([]);
    const [selectedVariableFunction, setSelectedVariableFunction] =
        React.useState(null);
    const [selectedQuestion, setSelectedQuestion] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [touched, setTouched] = React.useState({
        selectedReferenceQ: false,
        selectedOperator: false,
        value: false
    });

    React.useEffect(() => {
        if (props.rule.ruleID) {
            const variableFunction = IncaVariableFunctions.find(
                (variableFunction) =>
                    variableFunction.value ===
                    props.rule?.selectedOperator?.value
            );
            setSelectedVariableFunction(variableFunction);
        }
    }, []);

    /** Populate rule.compareOptions with targets, if doing target comparison but targets not yet populated. */
    React.useEffect(() => {
        if (
            props.rule?.compareTargetIds?.length &&
            !props.rule?.compareOptions?.length &&
            props.questionSummaries?.length
        ) {
            const allTargets = _.flatMap(
                props.questionSummaries,
                (q) => q.conversationalSettings?.smartProbeSettings?.targets
            ).filter((t) => t);
            props.rule.compareOptions = props.rule.compareTargetIds.map(
                (id) => {
                    const target = allTargets.find((t) => t.id == id);
                    return {
                        ...target,
                        value: target?.id
                    };
                }
            );
        }
    }, [props.rule, props.questionSummaries]);

    React.useEffect(() => {
        if (props.rule?.selectedReferenceQ?.id) {
            setOptions([]);
            if (resetSelectedOptions) setSelectedVariableFunction(null);
            getQuestion(props.rule.selectedReferenceQ.id);
            setResetSelectedOptions(false);
        }
    }, [props.rule.selectedReferenceQ?.value]);

    const getQuestion = async (questionId: number, piping = false) => {
        setIsLoading(true);
        let question;

        if (questionId > 0) {
            question = await props.getQuestionById(questionId, {
                logicList: true
            });
        } else {
            question =
                props.systemQuestionSummaries[
                    props.rule.selectedReferenceQ.value
                ];
        }
        setSelectedQuestion(question);
        const incaVariableEval = new IncaVariableEval(question);
        if (!piping) {
            setVariableFunctions(incaVariableEval.getIncaVariableFunction());
        }
        getOptions(incaVariableEval, piping);
        if (question?.piping) {
            const pipingQuestions = question?.pipingQuestions;

            await getQuestion(pipingQuestions[0].id as number, true);
        }
        setIsLoading(false);
    };
    const getOptions = (incaVariableEval, isPiping) => {
        const question = incaVariableEval.getQuestion() as Question;
        const options = incaVariableEval.getQuestionOption(
            props.currentProject
        );
        setTargets(
            question.conversationalSettings?.smartProbeSettings?.targets
        );

        if (isPiping) {
            setOptions((oldOptions) => [
                ...oldOptions,
                { label: `Piped question - ${question.title}`, options }
            ]);
        } else {
            setOptions((oldOptions) => [...oldOptions, ...options]);
        }
    };

    const setDirty = (keyName: string) => {
        setTouched((prevTouched) => ({ ...prevTouched, [keyName]: true }));
    };

    const getErrorMessage = (): string[] => {
        const { showError, errorObject } = props;
        const missingFields = [];
        if (
            (showError || touched.selectedReferenceQ) &&
            errorObject?.selectedReferenceQ
        ) {
            missingFields.push('Missing reference question');
        }

        if (
            (showError || touched.selectedOperator) &&
            errorObject?.selectedOperator
        ) {
            missingFields.push('Missing condition operator');
        }
        if ((showError || touched.value) && errorObject?.value) {
            missingFields.push('Missing condition options');
        }
        return missingFields;
    };

    const getRankOptions = (
        opts: any[]
    ): { label: string; value: number }[] => {
        return [
            { label: 'Any', value: 0 },
            ...Array.from(Array(opts.length).keys()).map((ind) => {
                const value = ind + 1;
                return { value, label: String(value) };
            })
        ];
    };

    const renderFooter = () => {
        switch (true) {
            case [
                INCA_VARIABLE_FUNCTION_TYPES.DOES_NOT_INCLUDE_RANK,
                INCA_VARIABLE_FUNCTION_TYPES.INCLUDES_WITH_RANK
            ].includes(selectedVariableFunction?.type):
                return (
                    <span>
                        in{' '}
                        <b>
                            {props.rule?.compareCustomValue?.value
                                ? `the ${getOrdinalNumber(
                                      props.rule?.compareCustomValue?.value
                                  )}`
                                : props.rule?.compareCustomValue?.label}{' '}
                            rank
                        </b>
                    </span>
                );
            case selectedQuestion?.type === QTYPES.TRADE_OFF &&
                [
                    INCA_VARIABLE_FUNCTION_TYPES.INCLUDES,
                    INCA_VARIABLE_FUNCTION_TYPES.DOES_NOT_INCLUDE
                ].includes(selectedVariableFunction?.type):
                return <span> in the top choice</span>;
            default:
                return null;
        }
    };
    const errorState =
        (props.showError || Object.values(touched).some((t) => t)) &&
        props.errorObject?.isError;
    const hasWhiteSpace = /\s/.test(props.rule?.compareCustomValue?.key || '');
    const questionLevelLogics = IncaVariableFunctions.filter((f) =>
        [
            INCA_VARIABLE_FUNCTION_TYPES.IS_DISPLAYED,
            INCA_VARIABLE_FUNCTION_TYPES.IS_NOT_DISPLAYED
        ].includes(f.type)
    );

    const isQuestionLevelLogic = questionLevelLogics
        .map((l) => l.value)
        .includes(props.rule?.selectedOperator?.value);

    return (
        <div
            className={classnames('condition--rule--body', {
                [props.className]: !!props.className,
                error: errorState && props.errorObject?.isError
            })}
        >
            {props.isEdit ? (
                <>
                    <div
                        className={classnames('condition--rule--row', {
                            [props.className]: !!props.className
                        })}
                    >
                        <span
                            className={classnames('condition--rule--title', {
                                error:
                                    (props.showError ||
                                        touched.selectedReferenceQ) &&
                                    props.errorObject?.selectedReferenceQ
                            })}
                        >
                            If someone answers to
                            {errorState && (
                                <>
                                    <span
                                        data-tip="custom show"
                                        data-for={`option-condition-error-${props.rule.ruleID}`}
                                        data-delay-show="500"
                                        className="condition--rule--error"
                                    >
                                        <UilExclamationTriangle size={16} />
                                    </span>
                                    <ReactTooltip
                                        className="tooltip"
                                        id={`option-condition-error-${props.rule.ruleID}`}
                                        ara-haspopup="true"
                                        effect="solid"
                                        place="top"
                                    >
                                        {getErrorMessage().map(
                                            (message, index) => (
                                                <p key={message + index}>
                                                    {message};
                                                </p>
                                            )
                                        )}
                                    </ReactTooltip>
                                </>
                            )}
                        </span>
                        <div className="form-group">
                            <IncaVariableSelect
                                availableQTypes={CONDITION_QTYPES}
                                includeCurrentQuestion={true}
                                handleQuestionSelect={(q) => {
                                    setResetSelectedOptions(true);
                                    props.handleQuestionSelect(q);
                                }}
                                selectedQuestion={
                                    props.rule?.selectedReferenceQ
                                }
                                disabled={props.disabled}
                            />
                        </div>
                    </div>
                    {props.rule?.selectedReferenceQ?.value ===
                        URL_METADATA_QUESTION_VALUE && (
                        <>
                            <div
                                className={classnames('condition--rule--row', {
                                    [props.className]: !!props.className
                                })}
                            >
                                <label className="form-group__label form-group__label--block">
                                    URL key
                                </label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={props.rule?.compareCustomValue?.key}
                                    onChange={(e) =>
                                        props.handleOptionSelect(
                                            'string',
                                            {
                                                ...props.rule
                                                    ?.compareCustomValue,
                                                key: e.target.value
                                            },
                                            CONDITION_TYPES.CUSTOM_VALUE
                                        )
                                    }
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                />
                            </div>{' '}
                            {hasWhiteSpace ? (
                                <Notes type={ALERT_MESSAGE_TYPES.WARN}>
                                    <span>
                                        URL Metadata shouldn&apos;t contain
                                        whitespace. Please modify it or click{' '}
                                        <span
                                            style={{
                                                color: '#2d67cb',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() =>
                                                props.handleOptionSelect(
                                                    'string',
                                                    {
                                                        ...props.rule
                                                            ?.compareCustomValue,
                                                        key: _.snakeCase(
                                                            props.rule
                                                                ?.compareCustomValue
                                                                ?.key
                                                        )
                                                    },
                                                    CONDITION_TYPES.CUSTOM_VALUE
                                                )
                                            }
                                        >
                                            <b>here</b>
                                        </span>{' '}
                                        to replace it to{' '}
                                        <b>
                                            {_.snakeCase(
                                                props.rule?.compareCustomValue
                                                    ?.key
                                            )}
                                        </b>
                                    </span>
                                </Notes>
                            ) : null}
                        </>
                    )}

                    <div className="d-flex">
                        <div
                            className={classnames('condition--rule--row', {
                                [props.className]: !!props.className
                            })}
                        >
                            <VariableFunctionEditor
                                showError={props.showError}
                                errorObject={props.errorObject}
                                touched={touched}
                                disabled={props.disabled}
                                selectedVariableFunction={
                                    selectedVariableFunction
                                }
                                variableFunctions={variableFunctions}
                                handleVariableFunctionSelect={(operator) => {
                                    setDirty('selectedOperator');
                                    setSelectedVariableFunction(operator);
                                    props.handleVariableFunctionSelect(
                                        operator
                                    );
                                }}
                                isLoading={isLoading}
                            />
                        </div>
                        <div
                            className={classnames('condition--rule--row', {
                                [props.className]: !!props.className
                            })}
                        >
                            <VariableOptionEditor
                                showError={props.showError}
                                errorObject={props.errorObject}
                                selectedVariableFunction={
                                    selectedVariableFunction
                                }
                                rule={props.rule}
                                disabled={props.disabled}
                                options={options}
                                targets={targets}
                                touched={touched}
                                handleOptionSelect={(
                                    type,
                                    option,
                                    compareType
                                ) => {
                                    setDirty('value');
                                    props.handleOptionSelect(
                                        type,
                                        option,
                                        compareType
                                    );
                                }}
                                isLoading={isLoading}
                                selectedQuestion={selectedQuestion}
                            />
                        </div>
                    </div>
                    {[
                        INCA_VARIABLE_FUNCTION_TYPES.DOES_NOT_INCLUDE_RANK,
                        INCA_VARIABLE_FUNCTION_TYPES.INCLUDES_WITH_RANK
                    ].includes(selectedVariableFunction?.type) ? (
                        <div className="d-flex">
                            <div
                                className={classnames('condition--rule--row', {
                                    [props.className]: !!props.className
                                })}
                            ></div>
                            <div
                                className={classnames('condition--rule--row', {
                                    [props.className]: !!props.className
                                })}
                            >
                                <VariableOptionEditor
                                    showError={props.showError}
                                    errorObject={props.errorObject}
                                    selectedVariableFunction={{ args: 'rank' }}
                                    rule={props.rule}
                                    disabled={props.disabled}
                                    options={getRankOptions(options)}
                                    targets={targets}
                                    touched={touched}
                                    handleOptionSelect={(
                                        type,
                                        option,
                                        compareType
                                    ) => {
                                        setDirty('value');
                                        props.handleOptionSelect(
                                            type,
                                            option,
                                            compareType
                                        );
                                    }}
                                    isLoading={isLoading}
                                />
                            </div>
                        </div>
                    ) : null}
                </>
            ) : (
                <div
                    className={classnames(
                        'condition--rule--row d-block flex-row',
                        {
                            [props.className]: !!props.className
                        }
                    )}
                >
                    {isQuestionLevelLogic ? 'When ' : 'In '}
                    <span
                        className={classnames('view--mode--question', {
                            error: !props.rule?.selectedReferenceQ?.label
                        })}
                    >
                        {props.rule?.selectedReferenceQ?.label || 'ERROR'}
                    </span>
                    {isQuestionLevelLogic ? '' : ', respondent answers '}
                    {props.rule?.selectedReferenceQ?.value ===
                    URL_METADATA_QUESTION_VALUE ? (
                        <>
                            <b>URL key</b> (
                            <span className="view--mode--option">
                                #{props.rule?.compareCustomValue?.key}
                            </span>
                            )
                        </>
                    ) : null}
                    <span className="view--mode--operator">
                        {uppercaseFirstLetter(
                            props.rule?.selectedOperator?.label || ''
                        )}
                    </span>
                    {selectedVariableFunction?.args === 'options' ||
                    selectedVariableFunction?.args === 'targets' ? (
                        props.rule.compareOptions?.map((opt, index) => (
                            <>
                                {props.rule.compareOptions.length > 1 &&
                                index === props.rule.compareOptions.length - 1
                                    ? ' or '
                                    : ''}
                                <span
                                    className={classnames(
                                        'view--mode--option',
                                        {
                                            error: !opt?.label
                                        }
                                    )}
                                    key={opt.value}
                                >
                                    {`  #${opt.label || 'ERROR'} `}
                                </span>
                                {index ===
                                    props.rule.compareOptions.length - 1 ||
                                index === props.rule.compareOptions.length - 2
                                    ? ''
                                    : ', '}
                            </>
                        ))
                    ) : selectedVariableFunction?.args === 'number' ? (
                        <span className="view--mode--option">
                            #{props.rule.compareNumber}
                        </span>
                    ) : selectedVariableFunction?.args === 'string' ? (
                        <span className="view--mode--option">
                            #{props.rule?.compareCustomValue?.value}
                        </span>
                    ) : null}
                    {renderFooter()}
                </div>
            )}
        </div>
    );
};

const mapStateToProps = ({ projectReducer }: IApplicationState) => {
    const { currentProject } = projectReducer;

    return {
        currentProject,
        projectID: currentProject.id,
        languages: currentProject.languages,
        systemQuestionSummaries: getSystemQuestionSummaries()
    };
};

const mapDispatchToProps = (dispatch) => ({
    getQuestionById: (questionId: number, options, logic) =>
        dispatch(getQuestionByQuestionId(questionId, options))
});

/**
 * This component provides UI for researcher to create an IncaVariableEval.
 */
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(IncaVariableEvalEditor);
